import { Box, Typography, Paper, Grid } from '@mui/material';
import type { PageTemplate, PaperSize } from '@/types/notebook';
import { TEMPLATE_NAMES, TEMPLATE_CONFIG, PAPER_SIZE_NAMES } from '@/types/notebook';

type TemplatePickerProps = {
  selectedTemplate: PageTemplate;
  onTemplateSelect: (template: PageTemplate) => void;
  selectedPaperSize?: PaperSize;
  onPaperSizeSelect?: (size: PaperSize) => void;
  showPaperSize?: boolean;
};

const TEMPLATES: PageTemplate[] = [
  'blank',
  'ruled-college',
  'ruled-wide',
  'grid-small',
  'grid-large',
  'dotted',
];

const PAPER_SIZES: PaperSize[] = ['a4', 'letter'];

/**
 * Mini preview of a template pattern
 */
const TemplatePreview = ({ template }: { template: PageTemplate }) => {
  const config = TEMPLATE_CONFIG[template];
  const width = 60;
  const height = 80;
  const previewScale = 0.5;

  const renderPattern = () => {
    const { spacing, color } = config;
    const scaledSpacing = spacing * previewScale;

    switch (config.type) {
      case 'lines':
        const lines = [];
        for (let y = scaledSpacing; y < height; y += scaledSpacing) {
          lines.push(
            <line
              key={y}
              x1={0}
              y1={y}
              x2={width}
              y2={y}
              stroke={color}
              strokeWidth={0.5}
            />
          );
        }
        return <>{lines}</>;

      case 'grid':
        const gridLines = [];
        for (let y = scaledSpacing; y < height; y += scaledSpacing) {
          gridLines.push(
            <line
              key={`h${y}`}
              x1={0}
              y1={y}
              x2={width}
              y2={y}
              stroke={color}
              strokeWidth={0.5}
            />
          );
        }
        for (let x = scaledSpacing; x < width; x += scaledSpacing) {
          gridLines.push(
            <line
              key={`v${x}`}
              x1={x}
              y1={0}
              x2={x}
              y2={height}
              stroke={color}
              strokeWidth={0.5}
            />
          );
        }
        return <>{gridLines}</>;

      case 'dots':
        const dots = [];
        for (let y = scaledSpacing / 2; y < height; y += scaledSpacing) {
          for (let x = scaledSpacing / 2; x < width; x += scaledSpacing) {
            dots.push(
              <circle
                key={`${x}-${y}`}
                cx={x}
                cy={y}
                r={0.75}
                fill={color}
              />
            );
          }
        }
        return <>{dots}</>;

      default:
        return null;
    }
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ backgroundColor: '#ffffff' }}
    >
      {renderPattern()}
    </svg>
  );
};

export const TemplatePicker = ({
  selectedTemplate,
  onTemplateSelect,
  selectedPaperSize,
  onPaperSizeSelect,
  showPaperSize = false,
}: TemplatePickerProps) => {
  return (
    <Box>
      {/* Paper Size Selection */}
      {showPaperSize && onPaperSizeSelect && selectedPaperSize && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Paper Size
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {PAPER_SIZES.map((size) => (
              <Paper
                key={size}
                onClick={() => onPaperSizeSelect(size)}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: size === selectedPaperSize ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  transition: 'border-color 0.2s',
                  '&:hover': {
                    borderColor: 'primary.light',
                  },
                }}
              >
                <Typography variant="body2">{PAPER_SIZE_NAMES[size]}</Typography>
              </Paper>
            ))}
          </Box>
        </Box>
      )}

      {/* Template Selection */}
      <Typography variant="subtitle2" gutterBottom>
        Page Template
      </Typography>
      <Grid container spacing={1.5}>
        {TEMPLATES.map((template) => (
          <Grid size={{ xs: 4 }} key={template}>
            <Paper
              onClick={() => onTemplateSelect(template)}
              sx={{
                p: 1,
                cursor: 'pointer',
                border: 2,
                borderColor: template === selectedTemplate ? 'primary.main' : 'divider',
                borderRadius: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'border-color 0.2s, box-shadow 0.2s',
                '&:hover': {
                  borderColor: 'primary.light',
                  boxShadow: 1,
                },
              }}
            >
              <Box
                sx={{
                  borderRadius: 0.5,
                  overflow: 'hidden',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <TemplatePreview template={template} />
              </Box>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  textAlign: 'center',
                  fontWeight: template === selectedTemplate ? 600 : 400,
                }}
              >
                {TEMPLATE_NAMES[template]}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
